import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatContactName, getGradeLabel, getPickupBadge, getStudentName, parseChildrenIds } from '@car-pool/shared/schoolRecords';
import { buildAuthorizedDrivers, getLinkedDriversForStudent, getLinkedParentsForStudent } from '@car-pool/shared/studentDetails';
import { apiFetch } from '../config/api';
import SchoolAvatar from '../components/SchoolAvatar';
import SchoolDirectoryRow from '../components/SchoolDirectoryRow';
import SchoolLayout from '../components/SchoolLayout';
import StudentProfileCard from '../components/StudentProfileCard';
import GradeSplitManager from '../components/GradeSplitManager';
import { buildStudentDriverInvitePayload, validateStudentDriverInviteForm } from '../lib/studentDriverInvite';

function formatTime(value, school = null) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const timeZoneMap = {
    Eastern: 'America/New_York',
    Central: 'America/Chicago',
    Mountain: school?.is_dst ? 'America/Denver' : 'America/Phoenix',
    Pacific: 'America/Los_Angeles',
    Alaska: 'America/Anchorage',
    Hawaii: 'Pacific/Honolulu',
  };
  const timeZone = timeZoneMap[school?.time_zone] || null;

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}
function compareStudents(left, right) {
  const leftKey = `${left.l_name || ''} ${left.f_name || ''}`.trim().toLowerCase();
  const rightKey = `${right.l_name || ''} ${right.f_name || ''}`.trim().toLowerCase();
  return leftKey.localeCompare(rightKey);
}

function isActive(value) {
  return value === true || value === 1 || String(value) === '1';
}

function isoDateForOffset(days = 0) {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function buildDriverAssignment() {
  return {
    mode: 'existing',
    authorizedContactId: '',
    search: '',
    startDate: isoDateForOffset(0),
    endDate: isoDateForOffset(1),
    saving: false,
    error: '',
    newDriver: {
      f_name: '',
      l_name: '',
      relationship: 'Friend',
      phone: '',
      email: '',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      driver_license_number: '',
      license_plate: '',
      vehicle_make_model: '',
      car_color: '',
    },
  };
}

function buildInviteState() {
  return {
    loading: false,
    error: '',
    result: null,
    copied: false,
    form: {
      fName: '',
      lName: '',
      phone: '',
      email: '',
      startDate: isoDateForOffset(0),
      endDate: isoDateForOffset(1),
    },
  };
}

const STUDENT_GROUPING_MODES = new Set(['grades', 'grades_and_splits', 'splits_only']);

function normalizeStudentGroupingMode(value) {
  return STUDENT_GROUPING_MODES.has(value) ? value : 'grades';
}

export default function StudentList({ token, navigate, schoolId, user, selectStudentId = null, bare = false }) {
  const [students, setStudents] = useState([]);
  const [dismissalStatuses, setDismissalStatuses] = useState([]);
  const [gradeNames, setGradeNames] = useState([]);
  const [gradeSplits, setGradeSplits] = useState([]);
  const [parentContacts, setParentContacts] = useState([]);
  const [driverContacts, setDriverContacts] = useState([]);
  const [busDriverIds, setBusDriverIds] = useState(() => new Set());
  const [school, setSchool] = useState(null);
  const [importReviewSummary, setImportReviewSummary] = useState(null);
  const [expandedGrade, setExpandedGrade] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(selectStudentId || null);

  useEffect(() => {
    if (selectStudentId) setSelectedStudentId(selectStudentId);
  }, [selectStudentId]);
  const [studentDetailCache, setStudentDetailCache] = useState({});
  const studentDetailCacheRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteState, setInviteState] = useState(null);
  const [driverAssignment, setDriverAssignment] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [studentsRes, dismissalRes, gradeNamesRes, gradeSplitsRes, contactsRes, busesRes, schoolRes, importReviewRes] = await Promise.all([
        apiFetch(`/students?school_id=${schoolId}`, {}, token),
        apiFetch(`/dismissal-status?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/school-grade-names?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/school-grade-splits?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/contacts?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/buses?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/schools/${schoolId}`, {}, token).catch(() => ({ data: null })),
        apiFetch(`/import-review/summary?school_id=${schoolId}`, {}, token).catch(() => ({ data: null })),
      ]);

      setStudents(studentsRes.data || []);
      setDismissalStatuses(dismissalRes.data || []);
      setGradeNames((gradeNamesRes.data || []).filter((grade) => grade.is_active));
      setGradeSplits(gradeSplitsRes.data || []);
      const schoolContacts = contactsRes.data || [];
      setParentContacts(schoolContacts.filter((contact) => ['parent', 'guardian'].includes(contact.type)));
      setDriverContacts(schoolContacts.filter((contact) => contact.type === 'driver'));
      setBusDriverIds(new Set((busesRes.data || []).map((bus) => Number(bus.driver_contact_id)).filter(Boolean)));
      setSchool(schoolRes.data || null);
      setImportReviewSummary(importReviewRes.data || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const activeStudents = useMemo(
    () => students.filter((student) => student.status !== 'inactive'),
    [students]
  );

  // The directory list + search show active students by default; when the
  // "Show deactivated" switch is on, deactivated students are included too.
  const directorySourceStudents = showInactive ? students : activeStudents;

  const dismissalByStudentId = useMemo(
    () => Object.fromEntries(dismissalStatuses.map((status) => [status.student_id, status])),
    [dismissalStatuses]
  );

  const gradeLabelMap = useMemo(() => {
    const entries = gradeNames.map((grade) => [Number(grade.grade_code), grade.label]);
    return Object.fromEntries(entries);
  }, [gradeNames]);

  const gradeSplitById = useMemo(() => {
    return Object.fromEntries(
      gradeSplits.map((split) => [Number(split.id), split])
    );
  }, [gradeSplits]);

  const getStudentSplit = useCallback(
    (student) => gradeSplitById[Number(student?.grade_split_id)] || null,
    [gradeSplitById]
  );

  const getStudentGradeDisplay = useCallback((student) => {
    const gradeLabel = getGradeLabel(student?.grade, gradeLabelMap);
    const split = getStudentSplit(student);
    return split?.split_code ? `${gradeLabel} - ${split.split_code}` : gradeLabel;
  }, [getStudentSplit, gradeLabelMap]);

  const defaultDismissalTime = school?.default_dismissal_time || null;
  const schoolTimeOptions = useMemo(() => ({
    timeZone: school?.time_zone,
    isDst: Boolean(Number(school?.is_dst)),
  }), [school?.time_zone, school?.is_dst]);

  const directoryStudents = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = directorySourceStudents.filter((student) => {
      if (!query) return true;

      const fields = [
        student.f_name,
        student.l_name,
        student.student_email,
        student.student_phone,
        getStudentGradeDisplay(student),
        getStudentSplit(student)?.split_code,
      ];

      return fields.some((field) => String(field || '').toLowerCase().includes(query));
    });

    return [...filtered].sort((left, right) => {
      const leftBadge = getPickupBadge(left, dismissalByStudentId[left.id], schoolTimeOptions);
      const rightBadge = getPickupBadge(right, dismissalByStudentId[right.id], schoolTimeOptions);
      const rankDiff = leftBadge.rank - rightBadge.rank;
      if (rankDiff !== 0) return rankDiff;

      const leftTime = dismissalByStudentId[left.id]?.picked_at || dismissalByStudentId[left.id]?.sent_at || dismissalByStudentId[left.id]?.called_at || '';
      const rightTime = dismissalByStudentId[right.id]?.picked_at || dismissalByStudentId[right.id]?.sent_at || dismissalByStudentId[right.id]?.called_at || '';
      const timeDiff = String(rightTime).localeCompare(String(leftTime));
      if (timeDiff !== 0) return timeDiff;

      return compareStudents(left, right);
    });
  }, [directorySourceStudents, dismissalByStudentId, getStudentGradeDisplay, getStudentSplit, schoolTimeOptions, search]);

  const studentGroupingMode = normalizeStudentGroupingMode(school?.student_grouping_mode);
  const groupingPanelCopy = useMemo(() => {
    if (studentGroupingMode === 'splits_only') {
      return {
        title: 'By Split',
        description: 'Students are grouped by split name across grades, with grade fallback for students without a split',
        empty: 'No split groups are available yet.',
      };
    }

    if (studentGroupingMode === 'grades_and_splits') {
      return {
        title: 'By Grade & Split',
        description: 'Students are grouped by grade with split sections inside each grade',
        empty: 'No grade groups are available yet.',
      };
    }

    return {
      title: 'By Grade',
      description: 'Students are grouped by grade',
      empty: 'No grade groups are available yet.',
    };
  }, [studentGroupingMode]);

  const gradeGroups = useMemo(() => {
    const studentsByGrade = new Map();

    activeStudents.forEach((student) => {
      const gradeCode = Number(student.grade);
      if (!studentsByGrade.has(gradeCode)) {
        studentsByGrade.set(gradeCode, []);
      }
      studentsByGrade.get(gradeCode).push(student);
    });

    const configuredGrades = [...new Map(
      gradeNames.map((grade) => {
        const gradeCode = Number(grade.grade_code);
        return [gradeCode, {
          gradeCode,
          label: grade.label || getGradeLabel(grade.grade_code, gradeLabelMap),
        }];
      })
    ).values()];

    const missingGrades = [...studentsByGrade.keys()]
      .filter((gradeCode) => !configuredGrades.some((grade) => grade.gradeCode === gradeCode))
      .map((gradeCode) => ({
        gradeCode,
        label: getGradeLabel(gradeCode, gradeLabelMap),
      }));

    const configuredAndMissingGrades = [...configuredGrades, ...missingGrades]
      .sort((left, right) => right.gradeCode - left.gradeCode)
      .map((grade) => ({
        ...grade,
        key: `grade:${grade.gradeCode}`,
      }));

    if (studentGroupingMode === 'splits_only') {
      const groupsByKey = new Map();

      activeStudents.forEach((student) => {
        const split = getStudentSplit(student);
        const gradeCode = Number(student.grade);
        const hasGrade = Number.isInteger(gradeCode);
        const splitCode = String(split?.split_code || '').trim();
        const key = splitCode
          ? `split-code:${splitCode.toLowerCase()}`
          : (hasGrade ? `grade-fallback:${gradeCode}` : 'grade-fallback:none');
        const label = splitCode || (hasGrade ? getGradeLabel(gradeCode, gradeLabelMap) : 'No grade assigned');
        const kind = splitCode ? 'split_code' : 'grade_fallback';

        if (!groupsByKey.has(key)) {
          groupsByKey.set(key, {
            key,
            kind,
            gradeCode: hasGrade ? gradeCode : null,
            label,
            active: split ? isActive(split.is_active) : true,
            students: [],
            splitGroups: [{
              key: 'split:none',
              label,
              active: split ? isActive(split.is_active) : true,
              students: [],
            }],
            showStudentGrade: kind === 'split_code',
            emptyLabel: kind === 'split_code'
              ? 'No students in this split yet.'
              : 'No students in this grade fallback yet.',
          });
        }

        const group = groupsByKey.get(key);
        if (split && !isActive(split.is_active)) {
          group.active = false;
          group.splitGroups[0].active = false;
        }
        group.students.push(student);
        group.splitGroups[0].students.push(student);
      });

      const splitGroups = [];
      const fallbackGroups = [];
      groupsByKey.forEach((group) => {
        const normalized = {
          ...group,
          students: [...group.students].sort((left, right) => {
            const gradeDiff = Number(left.grade) - Number(right.grade);
            if (gradeDiff !== 0) return gradeDiff;
            return compareStudents(left, right);
          }),
          splitGroups: group.splitGroups.map((splitGroup) => ({
            ...splitGroup,
            students: [...splitGroup.students].sort((left, right) => {
              const gradeDiff = Number(left.grade) - Number(right.grade);
              if (gradeDiff !== 0) return gradeDiff;
              return compareStudents(left, right);
            }),
          })),
        };

        if (normalized.kind === 'split_code') {
          splitGroups.push(normalized);
        } else {
          fallbackGroups.push(normalized);
        }
      });

      return [
        ...splitGroups.sort((left, right) => left.label.localeCompare(right.label)),
        ...fallbackGroups.sort((left, right) => Number(right.gradeCode) - Number(left.gradeCode)),
      ];
    }

    return configuredAndMissingGrades
      .map((grade) => {
        const gradeStudents = [...(studentsByGrade.get(grade.gradeCode) || [])].sort(compareStudents);

        if (studentGroupingMode === 'grades') {
          return {
            ...grade,
            students: gradeStudents,
            splitGroups: [{
              key: 'split:none',
              label: grade.label,
              active: true,
              students: gradeStudents,
            }],
            emptyLabel: 'No students in this grade yet.',
          };
        }

        const splitGroupsByKey = new Map();

        gradeStudents.forEach((student) => {
          const split = getStudentSplit(student);
          const key = split?.id ? `split:${split.id}` : 'split:none';
          if (!splitGroupsByKey.has(key)) {
            splitGroupsByKey.set(key, {
              key,
              label: split?.split_code || 'No split assigned',
              active: split ? isActive(split.is_active) : true,
              students: [],
            });
          }
          splitGroupsByKey.get(key).students.push(student);
        });

        const splitGroups = [...splitGroupsByKey.values()]
          .sort((left, right) => {
            if (left.key === 'split:none') return 1;
            if (right.key === 'split:none') return -1;
            return left.label.localeCompare(right.label);
          });

        return {
          ...grade,
          students: gradeStudents,
          splitGroups,
          emptyLabel: 'No students in this grade yet.',
        };
      })
      .filter((grade) => grade.students.length > 0 || configuredGrades.some((item) => item.gradeCode === grade.gradeCode));
  }, [activeStudents, getStudentSplit, gradeLabelMap, gradeNames, studentGroupingMode]);

  const recentPickups = useMemo(() => {
    return dismissalStatuses
      .filter((status) => ['picked_up', 'completed'].includes(String(status.status || '').toLowerCase()))
      .map((status) => ({
        ...status,
        student: students.find((student) => student.id === status.student_id) || null,
      }))
      .filter((item) => item.student)
      .sort((left, right) => String(right.picked_at || right.sent_at || '').localeCompare(String(left.picked_at || left.sent_at || '')));
  }, [dismissalStatuses, students]);

  const selectedStudent = useMemo(
    () => students.find((student) => student.id === selectedStudentId) || null,
    [selectedStudentId, students]
  );

  const selectedLinkedParents = useMemo(
    () => getLinkedParentsForStudent(selectedStudent, parentContacts),
    [parentContacts, selectedStudent]
  );

  const selectedLinkedDrivers = useMemo(
    () => getLinkedDriversForStudent(selectedStudent, driverContacts),
    [driverContacts, selectedStudent]
  );

  const selectedStudentDetail = selectedStudentId ? (studentDetailCache[selectedStudentId] || {}) : {};

  const selectedAuthorizedDrivers = useMemo(
    () => buildAuthorizedDrivers({
      student: selectedStudent,
      linkedParents: selectedLinkedParents,
      linkedDrivers: selectedLinkedDrivers,
      tempDrivers: selectedStudentDetail.tempDrivers || [],
    }),
    [selectedLinkedDrivers, selectedLinkedParents, selectedStudent, selectedStudentDetail.tempDrivers]
  );

  useEffect(() => {
    if (loading) return;
    if (!selectedStudentId) return;
    if (students.some((student) => student.id === selectedStudentId)) return;
    setSelectedStudentId(null);
  }, [loading, selectedStudentId, students]);

  useEffect(() => {
    if (!selectedStudentId) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedStudentId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedStudentId]);

  useEffect(() => {
    if (!selectedStudentId) {
      return undefined;
    }

    const studentId = selectedStudentId;
    const cachedDetail = studentDetailCacheRef.current[studentId];
    if (cachedDetail?.loaded || cachedDetail?.loading) return undefined;

    const loadingCache = {
      ...studentDetailCacheRef.current,
      [studentId]: {
        ...cachedDetail,
        loading: true,
        loaded: false,
        error: '',
      },
    };
    studentDetailCacheRef.current = loadingCache;
    setStudentDetailCache(loadingCache);

    (async () => {
      try {
        const [effectiveTimeRes, tempDriversRes] = await Promise.all([
          apiFetch(`/dismissal-time-defaults/effective?student_id=${studentId}`, {}, token).catch(() => ({ data: null })),
          apiFetch(`/temporary-authorized-drivers/student/${studentId}`, {}, token).catch(() => ({ data: [] })),
        ]);

        setStudentDetailCache((current) => {
          const next = {
            ...current,
            [studentId]: {
              loading: false,
              loaded: true,
              error: '',
              effectiveTime: effectiveTimeRes.data || null,
              tempDrivers: tempDriversRes.data || [],
            },
          };
          studentDetailCacheRef.current = next;
          return next;
        });
      } catch (err) {
        setStudentDetailCache((current) => {
          const next = {
            ...current,
            [studentId]: {
              ...current[studentId],
              loading: false,
              loaded: false,
              error: err.message,
              tempDrivers: current[studentId]?.tempDrivers || [],
            },
          };
          studentDetailCacheRef.current = next;
          return next;
        });
      }
    })();

    return undefined;
  }, [selectedStudentId, token]);

  const selectedCarpoolLine = selectedStudentDetail.effectiveTime?.carpool_line || null;
  const selectedNoSchool = Boolean(
    selectedStudentDetail.effectiveTime?.no_school
      || selectedStudentDetail.effectiveTime?.source === 'calendar_no_school'
  );
  const selectedDismissalTime = selectedNoSchool
    ? null
    : (selectedStudentDetail.effectiveTime?.dismissal_time || defaultDismissalTime);

  const inviteParent = selectedLinkedParents[0] || null;

  const assignedDriverIds = useMemo(
    () => new Set(
      (selectedStudentDetail.tempDrivers || [])
        .filter((record) => record.status === 'accepted')
        .map((record) => Number(record.authorized_contact_id))
        .filter(Boolean)
    ),
    [selectedStudentDetail.tempDrivers]
  );

  const assignableDrivers = useMemo(() => {
    const query = String(driverAssignment?.search || '').trim().toLowerCase();

    return driverContacts
      .filter((contact) => (
        busDriverIds.has(Number(contact.id))
        || parseChildrenIds(contact.children_ids).includes(Number(selectedStudent?.id))
      ))
      .filter((contact) => !assignedDriverIds.has(Number(contact.id)))
      .filter((contact) => {
        if (!query) return true;
        return [formatContactName(contact), contact.phone, contact.email]
          .some((value) => String(value || '').toLowerCase().includes(query));
      })
      .sort((left, right) => formatContactName(left).localeCompare(formatContactName(right)))
      .slice(0, 50);
  }, [assignedDriverIds, busDriverIds, driverAssignment?.search, driverContacts, selectedStudent?.id]);

  const openDriverAssignment = useCallback(() => {
    setDriverAssignment(buildDriverAssignment());
  }, []);

  const handleAssignDriver = useCallback(async () => {
    if (!selectedStudent || !driverAssignment) return;

    let authorizedContactId = Number(driverAssignment.authorizedContactId);
    if (driverAssignment.mode === 'existing' && !authorizedContactId) {
      setDriverAssignment((current) => ({ ...current, error: 'Select an authorized driver.' }));
      return;
    }
    if (driverAssignment.mode === 'new') {
      const requiredFields = [
        ['f_name', 'First name'],
        ['l_name', 'Last name'],
        ['phone', 'Phone'],
        ['email', 'Email'],
        ['street_address', 'Street address'],
        ['city', 'City'],
        ['state', 'State'],
        ['zip_code', 'ZIP code'],
      ];
      const missingField = requiredFields.find(([field]) => !String(driverAssignment.newDriver[field] || '').trim());
      if (missingField) {
        setDriverAssignment((current) => ({ ...current, error: `${missingField[1]} is required.` }));
        return;
      }
      if (String(driverAssignment.newDriver.state).trim().length !== 2) {
        setDriverAssignment((current) => ({ ...current, error: 'State must be a 2-letter code.' }));
        return;
      }
    }
    if (!driverAssignment.startDate || !driverAssignment.endDate) {
      setDriverAssignment((current) => ({ ...current, error: 'Start and end dates are required.' }));
      return;
    }
    if (new Date(`${driverAssignment.endDate}T00:00:00`) < new Date(`${driverAssignment.startDate}T00:00:00`)) {
      setDriverAssignment((current) => ({ ...current, error: 'End date must be on or after the start date.' }));
      return;
    }

    setDriverAssignment((current) => ({ ...current, saving: true, error: '' }));
    try {
      if (driverAssignment.mode === 'new') {
        const newDriver = driverAssignment.newDriver;
        const contactRes = await apiFetch('/contacts', {
          method: 'POST',
          body: JSON.stringify({
            type: 'driver',
            school_ids: [Number(schoolId)],
            children_ids: [Number(selectedStudent.id)],
            f_name: newDriver.f_name.trim(),
            l_name: newDriver.l_name.trim(),
            phone: newDriver.phone.trim(),
            email: newDriver.email.trim(),
            emergency_relationship: newDriver.relationship,
            street_address: newDriver.street_address.trim(),
            city: newDriver.city.trim(),
            state: newDriver.state.trim().toUpperCase(),
            zip_code: newDriver.zip_code.trim(),
            driver_license_number: newDriver.driver_license_number.trim(),
            photo_relative_path: 'placeholder.jpg',
            vehicle_make_model: newDriver.vehicle_make_model.trim() || null,
            car_color: newDriver.car_color.trim() || null,
            license_plate: newDriver.license_plate.trim().toUpperCase(),
            notifications_enabled: false,
          }),
        }, token);
        authorizedContactId = Number(contactRes.data?.id);
        if (!authorizedContactId) throw new Error('The driver account was created without a valid ID.');
        setDriverContacts((current) => [...current, contactRes.data]);
      }

      await apiFetch('/temporary-authorized-drivers', {
        method: 'POST',
        body: JSON.stringify({
          school_id: Number(schoolId),
          student_id: Number(selectedStudent.id),
          authorized_contact_id: authorizedContactId,
          start_date: driverAssignment.startDate,
          end_date: driverAssignment.endDate,
          status: 'accepted',
          notes: 'Assigned by school administrator',
        }),
      }, token);

      const tempDriversRes = await apiFetch(`/temporary-authorized-drivers/student/${selectedStudent.id}`, {}, token);
      setStudentDetailCache((current) => {
        const next = {
          ...current,
          [selectedStudent.id]: {
            ...(current[selectedStudent.id] || {}),
            loading: false,
            loaded: true,
            error: '',
            tempDrivers: tempDriversRes.data || [],
          },
        };
        studentDetailCacheRef.current = next;
        return next;
      });
      setDriverAssignment(null);
    } catch (err) {
      setDriverAssignment((current) => ({
        ...current,
        saving: false,
        error: err.message || 'Could not assign this driver.',
      }));
    }
  }, [driverAssignment, schoolId, selectedStudent, token]);

  const handleGenerateInvite = useCallback(async () => {
    if (!selectedStudent || !inviteParent) {
      setInviteState((current) => ({
        ...(current || buildInviteState()),
        loading: false,
        error: 'This student has no linked parent to authorize an invite.',
        result: null,
      }));
      return;
    }
    const validationError = validateStudentDriverInviteForm(inviteState?.form);
    if (validationError) {
      setInviteState((current) => ({ ...current, error: validationError }));
      return;
    }
    setInviteState((current) => ({ ...current, loading: true, error: '', result: null }));
    try {
      const res = await apiFetch('/driver-invites', {
        method: 'POST',
        body: JSON.stringify(buildStudentDriverInvitePayload({
          student: selectedStudent,
          parent: inviteParent,
          form: inviteState.form,
        })),
      }, token);
      setInviteState((current) => ({ ...current, loading: false, error: '', result: res.data }));
    } catch (err) {
      setInviteState((current) => ({ ...current, loading: false, error: err.message || 'Could not create the invite link.', result: null }));
    }
  }, [inviteParent, inviteState?.form, selectedStudent, token]);

  const handleInviteFieldChange = useCallback((field, value) => {
    setInviteState((current) => current ? {
      ...current,
      error: '',
      form: { ...current.form, [field]: value },
    } : current);
  }, []);

  const handleCopyInvite = useCallback(async () => {
    const link = inviteState?.result?.invite_url || inviteState?.result?.invite_path;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setInviteState((prev) => (prev ? { ...prev, copied: true } : prev));
    } catch {
      // clipboard unavailable; the link is still visible to copy manually
    }
  }, [inviteState]);

  const content = (
    <div className="sap-page">
        {error && <div className="sa-error sap-inline-error">{error}</div>}
        {importReviewSummary?.needs_review_total > 0 ? (
          <div className="sir-warning-banner">
            <div>
              <strong>{importReviewSummary.needs_review_total} imported records need review</strong>
              <span>Correct queued student and parent rows before adding them to the live roster.</span>
            </div>
            <button className="sa-btn sa-btn-primary" onClick={() => navigate('import-review', schoolId)}>
              Review Import Queue
            </button>
          </div>
        ) : null}

        <GradeSplitManager
          token={token}
          schoolId={schoolId}
          gradeNames={gradeNames}
          splits={gradeSplits}
          onChange={load}
        />

        <div className="sas-students-stage">
        {loading ? (
          <div className="sa-empty">Loading...</div>
        ) : (
          <>
          <div className={`sas-students-shell ${selectedStudent ? 'blurred' : ''}`}>
            <section className="sap-parents-panel sap-directory-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Students</h2>
                  <p>View, manage, and track all students scheduled for pickup</p>
                </div>
                <button className="sa-btn sa-btn-primary" onClick={() => navigate('student-create', schoolId)}>
                  + Add Student
                </button>
              </div>

              <div className="sap-directory-list">
                {directoryStudents.length === 0 ? (
                  <div className="sa-empty sap-compact-empty">
                    {directorySourceStudents.length === 0
                      ? (showInactive ? 'No students found for this school.' : 'No active students found for this school.')
                      : 'No students match your search.'}
                  </div>
                ) : (
                  directoryStudents.map((student) => {
                    const fullName = getStudentName(student);
                    const badge = getPickupBadge(student, dismissalByStudentId[student.id], schoolTimeOptions);

                    return (
                      <SchoolDirectoryRow
                        key={student.id}
                        record={student}
                        name={`${fullName} (${getStudentGradeDisplay(student)})`}
                        active={selectedStudentId === student.id}
                        className="sas-directory-row"
                        onClick={() => setSelectedStudentId(student.id)}
                      >
                        <span className={`sas-status-pill ${badge.tone}`}>
                          <span className="sas-status-pill-icon" aria-hidden="true"></span>
                          <span>{badge.label}</span>
                        </span>
                      </SchoolDirectoryRow>
                    );
                  })
                )}
              </div>

              <div className="sap-directory-footer">
                <button
                  type="button"
                  className="ssp-toggle-row sap-directory-inactive-toggle"
                  onClick={() => setShowInactive((prev) => !prev)}
                  aria-pressed={showInactive}
                >
                  <span>Show deactivated students</span>
                  <span className={`ssp-toggle ${showInactive ? 'on' : ''}`}>
                    <span className="ssp-toggle-knob"></span>
                  </span>
                </button>
                <label className="sap-directory-search">
                  <span>⌕</span>
                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search"
                  />
                </label>
              </div>
            </section>

            <section className="sap-parents-panel sas-grade-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>{groupingPanelCopy.title}</h2>
                  <p>{groupingPanelCopy.description}</p>
                </div>
              </div>

              <div className="sas-grade-list">
                {gradeGroups.length === 0 ? (
                  <div className="sa-empty sap-compact-empty">{groupingPanelCopy.empty}</div>
                ) : (
                  gradeGroups.map((group) => {
                    const isOpen = expandedGrade === group.key;
                    return (
                      <div key={group.key} className={`sas-grade-card ${isOpen ? 'open' : ''}`}>
                        <button
                          className="sas-grade-toggle"
                          onClick={() => setExpandedGrade(isOpen ? null : group.key)}
                        >
                          <div>
                            <div className="sas-grade-name">{group.label}</div>
                            <div className="sas-grade-count">{group.students.length} students</div>
                          </div>
                          <span className={`sas-grade-chevron ${isOpen ? 'open' : ''}`}>⌄</span>
                        </button>

                        {isOpen && (
                          <div className="sas-grade-roster">
                            {group.students.length === 0 ? (
                              <div className="sas-grade-empty">{group.emptyLabel || 'No students in this group yet.'}</div>
                            ) : (
                              group.splitGroups.map((splitGroup) => (
                                <div key={splitGroup.key} className="sas-split-group">
                                  {(group.splitGroups.length > 1 || splitGroup.key !== 'split:none') ? (
                                    <div className="sas-split-heading">
                                      <span>{splitGroup.label}</span>
                                      <strong>{splitGroup.students.length}</strong>
                                      {!splitGroup.active ? <em>Inactive</em> : null}
                                    </div>
                                  ) : null}

                                  {splitGroup.students.map((student) => {
                                    const fullName = getStudentName(student);
                                    const badge = getPickupBadge(student, dismissalByStudentId[student.id], schoolTimeOptions);
                                    const split = getStudentSplit(student);
                                    const statusText = student.status === 'active' ? (student.student_email || 'Active') : student.status;
                                    const detailParts = [];
                                    if (group.showStudentGrade) {
                                      detailParts.push(getGradeLabel(student.grade, gradeLabelMap));
                                    } else if (group.showStudentSplit && split?.split_code) {
                                      detailParts.push(split.split_code);
                                    }
                                    detailParts.push(statusText);

                                    return (
                                      <button
                                        key={student.id}
                                        className="sas-grade-student"
                                        onClick={() => setSelectedStudentId(student.id)}
                                      >
                                        <div className="sas-grade-student-main">
                                          <SchoolAvatar record={student} name={fullName} />
                                          <div className="sas-grade-student-copy">
                                            <span className="sap-student-name">{fullName}</span>
                                            <span className="sap-student-grade">{detailParts.filter(Boolean).join(' • ')}</span>
                                          </div>
                                        </div>
                                        <span className={`sas-status-pill ${badge.tone}`}>
                                          <span className="sas-status-pill-icon" aria-hidden="true"></span>
                                          <span>{badge.label}</span>
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className="sap-parents-panel sas-recent-panel">
              <div className="sap-panel-heading sas-recent-heading">
                <div>
                  <h2>Recent Pickups</h2>
                  <p>Latest completed pickups</p>
                </div>
              </div>

              <div className="sas-completed-pill">
                {recentPickups.length} completed
              </div>

              <div className="sas-recent-caption">Latest completed pickups</div>

              <div className="sas-recent-list">
                {recentPickups.length === 0 ? (
                  <div className="sa-empty sap-compact-empty">No students have been marked as picked up today.</div>
                ) : (
                  recentPickups.map((pickup) => {
                    const fullName = getStudentName(pickup.student);
                    return (
                      <button
                        key={`${pickup.student_id}-${pickup.picked_at || pickup.sent_at || pickup.id || fullName}`}
                        className="sas-recent-item"
                        onClick={() => setSelectedStudentId(pickup.student.id)}
                      >
                        <SchoolAvatar record={pickup.student} name={fullName} seed={pickup.student_id} />
                        <div className="sas-recent-copy">
                          <span className="sap-student-name">{fullName}</span>
                          <span className="sap-student-grade">{formatTime(pickup.picked_at || pickup.sent_at, school) || 'Completed'}</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </section>
          </div>

          {selectedStudent ? (
            <div className="sas-modal-overlay" onClick={() => setSelectedStudentId(null)}>
              <div className="sas-modal-popout" onClick={(event) => event.stopPropagation()}>
                <button type="button" className="sas-modal-back" onClick={() => setSelectedStudentId(null)}>
                  ←
                </button>
                <StudentProfileCard
                  student={selectedStudent}
                  school={school}
                  dismissalStatus={dismissalByStudentId[selectedStudent.id]}
                  gradeLabelMap={gradeLabelMap}
                  gradeSplit={getStudentSplit(selectedStudent)}
                  dismissalTime={selectedDismissalTime}
                  noSchool={selectedNoSchool}
                  carpoolLine={selectedCarpoolLine}
                  authorizedDrivers={selectedAuthorizedDrivers}
                  loadingDetails={!!selectedStudentDetail.loading}
                  detailsError={selectedStudentDetail.error}
                  onEdit={() => navigate('student-edit', { student: selectedStudent, schoolId })}
                  onInvite={() => setInviteState(buildInviteState())}
                  onAssignDriver={openDriverAssignment}
                />
              </div>
            </div>
          ) : null}

          {driverAssignment && selectedStudent ? (
            <div className="sas-modal-overlay" onClick={() => !driverAssignment.saving && setDriverAssignment(null)}>
              <div className="sas-modal-popout sap-authorization-modal" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  className="sas-modal-back"
                  onClick={() => setDriverAssignment(null)}
                  disabled={driverAssignment.saving}
                >
                  ←
                </button>
                <div className="sd-student-parent-modal-copy">
                  <h2>Assign Authorized Driver</h2>
                  <p>Select an existing driver or create a new driver for {selectedStudent.f_name} {selectedStudent.l_name}.</p>
                </div>

                {driverAssignment.error ? <div className="sa-error sap-inline-error">{driverAssignment.error}</div> : null}

                <div className="sa-btn-row">
                  <button
                    type="button"
                    className={`sa-btn ${driverAssignment.mode === 'existing' ? 'sa-btn-primary' : 'sa-btn-secondary'}`}
                    onClick={() => setDriverAssignment((current) => ({ ...current, mode: 'existing', error: '' }))}
                    disabled={driverAssignment.saving}
                  >
                    Existing Driver
                  </button>
                  <button
                    type="button"
                    className={`sa-btn ${driverAssignment.mode === 'new' ? 'sa-btn-primary' : 'sa-btn-secondary'}`}
                    onClick={() => setDriverAssignment((current) => ({ ...current, mode: 'new', error: '' }))}
                    disabled={driverAssignment.saving}
                  >
                    Create New Driver
                  </button>
                </div>

                {driverAssignment.mode === 'existing' ? (
                  <>
                    <label className="sap-authorization-field">
                      <span>Search school drivers</span>
                      <input
                        type="search"
                        value={driverAssignment.search}
                        onChange={(event) => setDriverAssignment((current) => ({ ...current, search: event.target.value }))}
                        placeholder="Name, phone, or email"
                        disabled={driverAssignment.saving}
                      />
                    </label>

                    <div className="sap-authorization-search-list">
                      {assignableDrivers.length ? assignableDrivers.map((contact) => {
                        const selected = Number(driverAssignment.authorizedContactId) === Number(contact.id);
                        const isBusDriver = busDriverIds.has(Number(contact.id));
                        return (
                          <button
                            type="button"
                            key={contact.id}
                            className="sap-authorization-search-row"
                            onClick={() => setDriverAssignment((current) => ({
                              ...current,
                              authorizedContactId: String(contact.id),
                              error: '',
                            }))}
                            disabled={driverAssignment.saving}
                            aria-pressed={selected}
                          >
                            <div className="sap-authorization-search-person">
                              <SchoolAvatar name={formatContactName(contact)} seed={contact.id} />
                              <div>
                                <div className="sap-authorization-name">{formatContactName(contact)}</div>
                                <div className="sap-authorization-meta">
                                  {isBusDriver ? 'School bus driver' : 'Student-specific driver'} · {contact.phone || contact.email || 'Driver account'}
                                </div>
                              </div>
                            </div>
                            <span className="sap-authorization-arrow">{selected ? '✓' : '→'}</span>
                          </button>
                        );
                      }) : (
                        <div className="sap-section-empty">
                          {driverContacts.length
                            ? 'No unassigned drivers match this search.'
                            : 'No eligible drivers exist yet. Create a student-specific driver here.'}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="sap-authorization-form">
                    <div className="ssp-email-bulk-note">
                      This creates a driver only for this student, such as a family friend or relative. Create and manage school-wide bus drivers on the Buses page.
                    </div>
                    <div className="sap-authorization-grid">
                      <label className="sap-authorization-field"><span>First Name *</span><input value={driverAssignment.newDriver.f_name} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, f_name: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                      <label className="sap-authorization-field"><span>Last Name *</span><input value={driverAssignment.newDriver.l_name} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, l_name: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                    </div>
                    <label className="sap-authorization-field"><span>Relationship</span><select value={driverAssignment.newDriver.relationship} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, relationship: event.target.value } }))} disabled={driverAssignment.saving}>{['Parent', 'Guardian', 'Sibling', 'Spouse', 'Relative', 'Friend', 'Neighbor', 'Other'].map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
                    <div className="sap-authorization-grid">
                      <label className="sap-authorization-field"><span>Phone *</span><input value={driverAssignment.newDriver.phone} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, phone: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                      <label className="sap-authorization-field"><span>Email *</span><input type="email" value={driverAssignment.newDriver.email} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, email: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                    </div>
                    <label className="sap-authorization-field"><span>Street Address *</span><input value={driverAssignment.newDriver.street_address} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, street_address: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                    <div className="sap-authorization-grid sap-authorization-grid-third">
                      <label className="sap-authorization-field"><span>City *</span><input value={driverAssignment.newDriver.city} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, city: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                      <label className="sap-authorization-field"><span>State *</span><input maxLength="2" value={driverAssignment.newDriver.state} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, state: event.target.value.toUpperCase() } }))} disabled={driverAssignment.saving} /></label>
                      <label className="sap-authorization-field"><span>ZIP *</span><input value={driverAssignment.newDriver.zip_code} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, zip_code: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                    </div>
                    <div className="sap-authorization-grid">
                      <label className="sap-authorization-field"><span>Driver License</span><input value={driverAssignment.newDriver.driver_license_number} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, driver_license_number: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                      <label className="sap-authorization-field"><span>License Plate</span><input value={driverAssignment.newDriver.license_plate} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, license_plate: event.target.value.toUpperCase() } }))} disabled={driverAssignment.saving} /></label>
                    </div>
                    <div className="sap-authorization-grid">
                      <label className="sap-authorization-field"><span>Vehicle Make/Model</span><input value={driverAssignment.newDriver.vehicle_make_model} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, vehicle_make_model: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                      <label className="sap-authorization-field"><span>Car Color</span><input value={driverAssignment.newDriver.car_color} onChange={(event) => setDriverAssignment((current) => ({ ...current, newDriver: { ...current.newDriver, car_color: event.target.value } }))} disabled={driverAssignment.saving} /></label>
                    </div>
                  </div>
                )}

                <div className="sap-authorization-grid">
                  <label className="sap-authorization-field">
                    <span>Start Date</span>
                    <input
                      type="date"
                      value={driverAssignment.startDate}
                      onChange={(event) => setDriverAssignment((current) => ({ ...current, startDate: event.target.value }))}
                      disabled={driverAssignment.saving}
                    />
                  </label>
                  <label className="sap-authorization-field">
                    <span>End Date</span>
                    <input
                      type="date"
                      value={driverAssignment.endDate}
                      onChange={(event) => setDriverAssignment((current) => ({ ...current, endDate: event.target.value }))}
                      disabled={driverAssignment.saving}
                    />
                  </label>
                </div>

                <div className="sa-btn-row">
                  <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setDriverAssignment(null)} disabled={driverAssignment.saving}>
                    Cancel
                  </button>
                  <button type="button" className="sa-btn sa-btn-primary" onClick={handleAssignDriver} disabled={driverAssignment.saving}>
                    {driverAssignment.saving
                      ? (driverAssignment.mode === 'new' ? 'Creating & Assigning…' : 'Assigning…')
                      : (driverAssignment.mode === 'new' ? 'Create & Assign Driver' : 'Assign Driver')}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {inviteState ? (
            <div className="sas-modal-overlay" onClick={() => setInviteState(null)}>
              <div className={`sas-modal-popout sas-invite-modal ${inviteState.result ? '' : 'sas-invite-modal-form'}`} onClick={(event) => event.stopPropagation()}>
                <h3 className="sas-invite-title">Invite a Driver</h3>
                {inviteState.result ? (
                  <>
                    <p className="sas-invite-help">
                      Share this link with the driver. They'll add their car details and enable location sharing on
                      {inviteParent ? ` ${inviteParent.f_name} ${inviteParent.l_name}'s` : "the parent's"} behalf.
                    </p>
                    <div className="sas-invite-linkbox">{inviteState.result.invite_url || inviteState.result.invite_path}</div>
                    <div className="sa-btn-row">
                      <button type="button" className="sa-btn sa-btn-primary" onClick={handleCopyInvite}>
                        {inviteState.copied ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setInviteState(null)}>
                        Done
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="sas-invite-help">
                      Generate a temporary pickup link for <strong>{selectedStudent ? `${selectedStudent.f_name} ${selectedStudent.l_name}` : 'this student'}</strong>
                      {inviteParent ? <>, authorized by <strong>{inviteParent.f_name} {inviteParent.l_name}</strong>.</> : '.'}
                    </p>
                    <div className="sap-authorization-grid sas-invite-fields">
                      <label className="sap-authorization-field">
                        <span>Driver First Name *</span>
                        <input value={inviteState.form.fName} onChange={(event) => handleInviteFieldChange('fName', event.target.value)} disabled={inviteState.loading} autoFocus />
                      </label>
                      <label className="sap-authorization-field">
                        <span>Driver Last Name *</span>
                        <input value={inviteState.form.lName} onChange={(event) => handleInviteFieldChange('lName', event.target.value)} disabled={inviteState.loading} />
                      </label>
                    </div>
                    <div className="sap-authorization-grid sas-invite-fields">
                      <label className="sap-authorization-field">
                        <span>Phone</span>
                        <input type="tel" value={inviteState.form.phone} onChange={(event) => handleInviteFieldChange('phone', event.target.value)} disabled={inviteState.loading} />
                      </label>
                      <label className="sap-authorization-field">
                        <span>Email</span>
                        <input type="email" value={inviteState.form.email} onChange={(event) => handleInviteFieldChange('email', event.target.value)} disabled={inviteState.loading} />
                      </label>
                    </div>
                    <div className="sap-authorization-grid sas-invite-fields">
                      <label className="sap-authorization-field">
                        <span>Start Date *</span>
                        <input type="date" value={inviteState.form.startDate} onChange={(event) => handleInviteFieldChange('startDate', event.target.value)} disabled={inviteState.loading} />
                      </label>
                      <label className="sap-authorization-field">
                        <span>End Date *</span>
                        <input type="date" value={inviteState.form.endDate} min={inviteState.form.startDate} onChange={(event) => handleInviteFieldChange('endDate', event.target.value)} disabled={inviteState.loading} />
                      </label>
                    </div>
                    <div className="sas-invite-contact-hint">Enter at least a phone number or email so the invitation can reach the driver.</div>
                    {inviteState.error ? <div className="sa-error">{inviteState.error}</div> : null}
                    <div className="sa-btn-row">
                      <button
                        type="button"
                        className="sa-btn sa-btn-primary"
                        onClick={handleGenerateInvite}
                        disabled={inviteState.loading || !inviteParent}
                      >
                        {inviteState.loading ? 'Generating…' : 'Generate Link'}
                      </button>
                      <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setInviteState(null)}>
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}
          </>
        )}
        </div>
      </div>
  );

  if (bare) return content;

  return (
    <SchoolLayout
      token={token}
      navigate={navigate}
      schoolId={schoolId}
      user={user}
      activePage="students"
      breadcrumb="Students"
    >
      {content}
    </SchoolLayout>
  );
}
